import { isNotDefined } from '@sgme/fp';
import { Button, HStack } from '@sgme/ui';
import clsx from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { getQuote, selectOptionIsReversed, selectOptionQuoteLegs } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';
import { definedEntries } from '@/utils/object.ts';

type CollapseStatus = 'collapsed' | 'expanded';

type MarketParametersProps = {
  tileId: string;
  onClick: (collapseMarketParametersStatus: CollapseStatus) => void;
  expanded?: boolean;
};

export function MarketParameters({ tileId, onClick, expanded }: MarketParametersProps) {
  const { formatNumber } = useIntl();
  const collapseMarketParametersStatus = expanded ? 'expanded' : 'collapsed';

  const isExpired = useOptionSelector((option) => option.properties.rfs.status === 'EXPIRED');
  const { marketData } = useOptionSelector((tile) => {
    const legId: LegId = `${tileId}/0`;
    const marketData = selectOptionQuoteLegs(tile, legId)?.marketData;

    return {
      marketData,
    };
  });

  const handleCollapseMarketParameters = () => {
    const newStatus = collapseMarketParametersStatus === 'collapsed' ? 'expanded' : 'collapsed';
    onClick(newStatus);
  };

  return (
    <>
      <HStack verticalAlignment="center" fullWidth className="px-2">
        <div className="flex-fill border-top" style={{ height: 1 }} />

        <Button
          size="sm"
          purpose="flat"
          onClick={handleCollapseMarketParameters}
          data-e2e="market-parameters-toggle-button"
        >
          <FormattedMessage id="option.sections.marketParameters" />
          <i className="icon icon-sm">
            {collapseMarketParametersStatus === 'collapsed' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          </i>
        </Button>

        <div className="flex-fill border-top" style={{ height: 1 }} />
      </HStack>

      {collapseMarketParametersStatus === 'expanded' && marketData && (
        <>
          <OptionFormGroup
            label={<FormattedMessage id="option.fields.volatility" />}
            className={clsx({ expired: isExpired })}
          >
            <Volatility />
          </OptionFormGroup>

          <OptionFormGroup
            label={<FormattedMessage id="option.fields.forward" />}
            className={clsx({ expired: isExpired })}
          >
            <span data-e2e="forward">
              {formatNumber(marketData.forward.bid, { maximumFractionDigits: 6, minimumFractionDigits: 6 })} /{' '}
              {formatNumber(marketData.forward.ask, { maximumFractionDigits: 6, minimumFractionDigits: 6 })}
            </span>
          </OptionFormGroup>

          <OptionFormGroup
            label={<FormattedMessage id="option.fields.swapPoints" />}
            className={clsx({ expired: isExpired })}
          >
            <span data-e2e="swap-points">
              {formatNumber(marketData.swapPoint.bid, { maximumFractionDigits: 2, minimumFractionDigits: 2 })} /{' '}
              {formatNumber(marketData.swapPoint.ask, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
            </span>
          </OptionFormGroup>
        </>
      )}
    </>
  );
}

function Volatility() {
  const { formatNumber } = useIntl();
  const { marketLegsData, productName, isReversed } = useOptionSelector((option) => {
    const quote = getQuote(option);
    const legsData = quote?.legsData;
    const isReversed = selectOptionIsReversed(option);

    const marketLegsData = definedEntries(legsData ?? {}).map(([_mpLegId, quoteLeg]) => {
      return quoteLeg.marketData.volatility;
    });

    return {
      marketLegsData,
      productName: option.type,
      isReversed,
    };
  });

  const format = useCallback(
    (value: number | undefined, precision = 2) => {
      if (isNotDefined(value)) {
        return '-';
      }

      return formatNumber(value, { maximumFractionDigits: precision, minimumFractionDigits: precision });
    },
    [formatNumber],
  );

  if (productName === 'RiskReversal') {
    const [call, put] = marketLegsData;
    return isReversed ? (
      <span data-e2e="volatility">
        {format(call?.ask)} / {format(put?.bid)} | {format(call?.bid)} / {format(put?.ask)}
      </span>
    ) : (
      <span data-e2e="volatility">
        {format(call?.bid)} / {format(put?.ask)} | {format(call?.ask)} / {format(put?.bid)}
      </span>
    );
  }

  const [marketData] = marketLegsData;

  return (
    <span data-e2e="volatility">
      {format(marketData?.bid, 3)} / {format(marketData?.ask, 3)}
    </span>
  );
}
